import React, { useEffect, useState } from "react";
import styles from "../../styles/__prescriptionPreview.module.scss";
import { useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useDispatch, useSelector } from "react-redux";
import { savePrescription } from "../../reducer/savePrescription/savePrescriptionReducer";
import { formatDateNumeric as tzDateNumeric } from "../../utils/datetime";
import { useTranslation } from "react-i18next";

export default function PrescriptionPreview({
  prescriptionData,
  prescriptionType = "medication",
  pharmacyCategories,
  onClose,
  onGenerateOnly,
}) {
  const {
    patient_id,
    user_id,
    prescription_type,
    medical_condition,
    medications = [],
    lab_tests = [],
    imaging_tests = [],
    physio_tests = [],
    nurse_tests = [],
    prescription_date,
    upload_signature_s3_key,
    upload_stamp_s3_key,
    stamp_presigned_url,
    signature_presigned_url,
    show_credentials,
    age,
    gender,
    patient_name,
    stamp,
    signature,
    date_of_birth,
    guardian_name,
    doctor_name,
    doctor_education,
    doctor_clinic_name,
    is_arabic,
  } = prescriptionData || {};

  // The printed prescription uses the language it was created in (is_arabic),
  // independent of later UI language changes.
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(is_arabic ? "ar" : "en");

  const previewRef = useRef(null);
  const prescriptionContentRef = useRef(null); // Ref for prescription content only (excluding fileCol)
  const [fileName, setFileName] = useState(patient_name || "");
  const [fileData, setFileData] = useState("");
  const [hasInitiatedSave, setHasInitiatedSave] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const dispatch = useDispatch();
  const savePrescriptionState = useSelector((state) => state.savePrescription);

  // Debug: Log prescription data when it changes
  useEffect(() => {
    console.log("PrescriptionPreview - prescriptionData:", {
      stamp_presigned_url,
      signature_presigned_url,
      stamp,
      signature,
      upload_stamp_s3_key,
      upload_signature_s3_key,
    });
  }, [
    stamp_presigned_url,
    signature_presigned_url,
    stamp,
    signature,
    upload_stamp_s3_key,
    upload_signature_s3_key,
  ]);

  // Helper function to convert image URL to base64 using fetch (avoids CORS issues)
  const convertImageToBase64 = async (url) => {
    try {
      console.log(
        "Fetching image for base64 conversion:",
        url.substring(0, 100) + "...",
      );

      // Use fetch to get the image as a blob
      // For presigned URLs, fetch should work even without CORS headers
      const response = await fetch(url, {
        method: "GET",
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch image: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      console.log("Image fetched successfully, converting blob to base64...");
      const base64 = await blobToBase64(blob);
      return base64;
    } catch (error) {
      console.error("Error fetching image with fetch API:", error);
      console.log(
        "Trying fallback method with Image element (may have CORS issues)...",
      );

      // Fallback: Try using Image element (may fail due to CORS)
      return new Promise((resolve, reject) => {
        const img = new Image();
        // Don't set crossOrigin - let browser handle it naturally
        // This might work if the server allows it

        let timeoutId;
        const timeout = 15000; // 15 second timeout

        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          img.onload = null;
          img.onerror = null;
        };

        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL("image/png");
            cleanup();
            console.log("Fallback method succeeded");
            resolve(base64);
          } catch (err) {
            console.error("Error converting image to base64 in fallback:", err);
            cleanup();
            reject(err);
          }
        };

        img.onerror = (err) => {
          console.error(
            "Error loading image in fallback (likely CORS issue):",
            err,
          );
          cleanup();
          reject(
            new Error(
              `Failed to load image: ${url}. CORS may not be configured on the server.`,
            ),
          );
        };

        timeoutId = setTimeout(() => {
          console.error("Timeout loading image in fallback");
          cleanup();
          reject(new Error(`Timeout loading image: ${url}`));
        }, timeout);

        // Try without crossOrigin first
        img.src = url;
      });
    }
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        console.log(
          "Successfully converted blob to base64, length:",
          base64.length,
        );
        resolve(base64);
      };
      reader.onerror = (error) => {
        console.error("Error reading blob:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  };

  // Convert all external images in element to base64
  const convertImagesToBase64 = async (element) => {
    if (!element) return element;

    // Create a deep clone of the element to avoid affecting the original preview
    const clonedElement = element.cloneNode(true);

    // Ensure cloned element maintains its structure and classes
    // CSS modules should work fine as cloneNode copies all attributes including class names
    // Just ensure visibility for proper rendering
    clonedElement.style.visibility = "visible";
    clonedElement.style.opacity = "1";

    // Find all images in the cloned element
    const images = clonedElement.querySelectorAll("img");
    const imagePromises = Array.from(images).map(async (img, index) => {
      // Get src from multiple possible sources
      let src =
        img.src || img.getAttribute("src") || img.getAttribute("data-src");

      // If still no src, try to get from original element's corresponding image
      if (!src && element.querySelectorAll("img")[index]) {
        const originalImg = element.querySelectorAll("img")[index];
        src =
          originalImg.src ||
          originalImg.getAttribute("src") ||
          originalImg.getAttribute("data-src");
      }

      // Skip if already base64 or data URL, or if no src
      if (!src || src.startsWith("data:") || src.startsWith("blob:")) {
        return;
      }

      // Skip if it's a relative path (like /no_image.jpeg)
      if (src.startsWith("/")) {
        return;
      }

      try {
        console.log("Converting image to base64:", src);
        // Convert presigned URL to base64
        const base64 = await convertImageToBase64(src);
        img.src = base64;
        // Remove crossOrigin attribute as base64 doesn't need it
        img.removeAttribute("crossOrigin");
        console.log("Successfully converted image to base64");
      } catch (error) {
        console.error("Failed to convert image to base64:", src, error);
        // Try to keep original src if conversion fails, but set crossOrigin
        if (src) {
          img.src = src;
          img.setAttribute("crossOrigin", "anonymous");
        }
      }
    });

    // Wait for all images to be converted
    await Promise.all(imagePromises);

    // Wait a bit for images to render in the cloned element
    await new Promise((resolve) => setTimeout(resolve, 500));

    return clonedElement;
  };

  const createPrescriptionFileData = async (useContentOnly = false) => {
    // Use prescriptionContentRef if generating only (excludes fileCol), otherwise use previewRef
    const originalElement = useContentOnly
      ? prescriptionContentRef.current
      : previewRef.current;
    if (!originalElement) return null;

    try {
      // Convert images to base64 using cloned element
      const element = await convertImagesToBase64(originalElement);

      // Temporarily append cloned element to body for html2canvas
      // Make it visible but off-screen to ensure all styles render properly
      const originalWidth =
        originalElement.offsetWidth || originalElement.scrollWidth;
      const originalHeight =
        originalElement.offsetHeight || originalElement.scrollHeight;

      element.style.position = "absolute";
      element.style.left = "-9999px";
      element.style.top = "0";
      element.style.visibility = "visible";
      element.style.opacity = "1";
      element.style.display = "block";

      // Preserve original dimensions to ensure borders and padding render correctly
      if (originalWidth) {
        element.style.width = `${originalWidth}px`;
        element.style.minWidth = `${originalWidth}px`;
        element.style.maxWidth = `${originalWidth}px`;
      }
      if (originalHeight) {
        element.style.height = "auto"; // Let height be calculated naturally
        element.style.minHeight = `${originalHeight}px`;
      }

      document.body.appendChild(element);

      // Wait a bit to ensure styles are applied and element is fully rendered
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Force a reflow to ensure all styles are computed
      void element.offsetHeight;

      try {
        // Get actual dimensions
        const elementWidth =
          element.scrollWidth ||
          element.offsetWidth ||
          originalElement.scrollWidth ||
          originalElement.offsetWidth;
        const elementHeight =
          element.scrollHeight ||
          element.offsetHeight ||
          originalElement.scrollHeight ||
          originalElement.offsetHeight;

        console.log("Element dimensions for PDF:", {
          width: elementWidth,
          height: elementHeight,
        });

        // Generate canvas from cloned element with base64 images
        // Use higher scale and better options to capture all styles including borders
        const canvas = await html2canvas(element, {
          scale: 2, // Higher scale for better quality
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: elementWidth,
          height: elementHeight,
          allowTaint: false,
          // Ensure all CSS styles are captured including borders, padding, etc.
          // foreignObjectRendering provides better CSS support
          foreignObjectRendering: false, // Set to false for better compatibility
        });

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calculate PDF dimensions (A4 size in mm)
        const pdfWidth = 210; // A4 width in mm
        const pdfHeight = 297; // A4 height in mm
        const imgAspectRatio = imgWidth / imgHeight;

        let finalWidth = pdfWidth;
        let finalHeight = pdfWidth / imgAspectRatio;

        // If height exceeds A4, scale down to fit
        if (finalHeight > pdfHeight) {
          finalHeight = pdfHeight;
          finalWidth = pdfHeight * imgAspectRatio;
        }

        // Create PDF
        const pdf = new jsPDF("p", "mm", "a4");
        const imgData = canvas.toDataURL("image/png");

        // Add image to PDF
        pdf.addImage(imgData, "PNG", 0, 0, finalWidth, finalHeight);

        // Convert PDF to base64 data URL
        const pdfDataUrl = pdf.output("dataurlstring");
        const fileSizeMB = ((pdfDataUrl.length * 3) / 4 / 1024 / 1024).toFixed(
          2,
        );
        console.log("Prescription PDF generated, size:", fileSizeMB, "MB");

        return pdfDataUrl;
      } finally {
        // Always remove cloned element from DOM
        if (element && element.parentNode) {
          element.parentNode.removeChild(element);
        }
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  useEffect(() => {
    const generatePdf = async () => {
      if (!previewRef.current) return;

      const pdfBase64 = await createPrescriptionFileData();
      setFileData(pdfBase64);
    };

    // Reset save flag when preview opens (new preview)
    setHasInitiatedSave(false);

    generatePdf();
  }, [prescriptionData]);

  useEffect(() => {
    if (patient_name) {
      setFileName(patient_name);
    }
  }, [patient_name]);

  // Close both preview and modal popups immediately on successful save
  useEffect(() => {
    if (
      hasInitiatedSave &&
      savePrescriptionState.data &&
      !savePrescriptionState.loading &&
      !savePrescriptionState.error &&
      onClose
    ) {
      // Close immediately - toast notification will remain visible even after modal closes
      onClose(true);
    }
  }, [
    hasInitiatedSave,
    savePrescriptionState.data,
    savePrescriptionState.loading,
    savePrescriptionState.error,
    onClose,
  ]);

  const handleGenerateOnly = async () => {
    if (!prescriptionContentRef.current) {
      console.error("No prescription content available");
      return;
    }

    setIsGenerating(true);

    try {
      // Wait for images (stamp and signature) to load before capturing
      const images = prescriptionContentRef.current.querySelectorAll("img");
      const imagePromises = Array.from(images).map((img) => {
        // Skip if image is already loaded or if it's a data URL/blob URL
        const src = img.src || img.getAttribute("src");
        if (img.complete && img.naturalHeight !== 0) {
          return Promise.resolve();
        }
        if (src && (src.startsWith("data:") || src.startsWith("blob:"))) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          // Set crossOrigin for CORS
          if (
            src &&
            !src.startsWith("/") &&
            !src.startsWith("data:") &&
            !src.startsWith("blob:")
          ) {
            img.crossOrigin = "anonymous";
          }

          if (img.complete && img.naturalHeight !== 0) {
            resolve();
            return;
          }

          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to not block generation
          // Timeout after 10 seconds
          setTimeout(() => resolve(), 10000);
        });
      });

      await Promise.all(imagePromises);

      // Small delay to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Generate PDF from content only (excluding fileCol)
      // This will convert images to base64 internally
      const pdfBase64 = await createPrescriptionFileData(true);

      if (!pdfBase64) {
        console.error("Failed to generate prescription PDF");
        return;
      }

      // Generate file name with .pdf extension
      const timestamp = new Date().getTime();
      const pdfFileName = fileName
        ? fileName.endsWith(".pdf")
          ? fileName
          : `${fileName}.pdf`
        : `${patient_name || "prescription"}_${timestamp}.pdf`;

      // Call the callback with generated prescription data
      if (onGenerateOnly) {
        onGenerateOnly({
          fileData: pdfBase64,
          fileName: pdfFileName,
        });
      }
    } catch (error) {
      console.error("Error generating prescription:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSavePrescription = async () => {
    if (!fileData) {
      console.error("No prescription file data available");
      return;
    }

    // Mark that we've initiated a save
    setHasInitiatedSave(true);

    // Generate file name with .pdf extension
    const timestamp = new Date().getTime();
    const pdfFileName = fileName
      ? fileName.endsWith(".pdf")
        ? fileName
        : `${fileName}.pdf`
      : `${patient_name || "prescription"}_${timestamp}.pdf`;

    try {
      // Convert data URL to Blob
      const response = await fetch(fileData);
      const blob = await response.blob();

      // Create a File object from the blob
      const pdfFile = new File([blob], pdfFileName, {
        type: "application/pdf",
      });

      // Create FormData for multipart/form-data upload
      const formData = new FormData();
      formData.append("patient_id", patient_id.toString());
      formData.append("user_id", user_id.toString()); // This is the doctor's user_id
      formData.append("file_name", pdfFileName);
      formData.append("file", pdfFile);
      // Add prescription_type based on the active tab
      if (prescriptionType) {
        const typeMap = {
          medication: "Medications",
          lab: "Lab_Test",
          imaging: "Imaging_Center",
          physiotherapy: "Physiotherapy Test",
          nurse: "Nurse_Service",
        };
        formData.append(
          "prescription_type",
          typeMap[prescriptionType] || "Medications",
        );
      }
      // Add doctor_name
      if (doctor_name) {
        formData.append("doctor_name", doctor_name);
      }
      // Add appointment_id if provided
      if (prescriptionData?.appointment_id) {
        formData.append(
          "appointment_id",
          prescriptionData.appointment_id.toString(),
        );
      }

      dispatch(savePrescription(formData));
    } catch (error) {
      console.error("Error converting PDF to file:", error);
      setHasInitiatedSave(false);
    }
  };

  return (
    <div
      className={styles["prescription-preview-container"]}
      ref={previewRef}
      dir={is_arabic ? "rtl" : "ltr"}
    >
      <div className={styles["infoCol"]} ref={prescriptionContentRef}>
        <div className={styles["infoCol-header"]}>
          <div className={styles["infoHeaderLogo"]}>
            {/* <img src={Logo} alt="Tiryak" /> */}
            <h3 className={styles["headerTitle"]}>
              {t("prescription.preview.doctorName", {
                name: doctor_name || t("common.na"),
              })}
            </h3>
            <p>{doctor_clinic_name || t("common.na")}</p>
          </div>
          <div className={styles["docInfo"]}>
            <p>{doctor_education || t("common.na")}</p>
            {/* <p>Reg. No.: BG-1234</p> */}
          </div>
        </div>

        <hr />

        <div className={styles["patientInfoWrapper"]}>
          <div className={styles["infoTopBar"]}>
            <div className={styles["patientInfo"]}>
              <b>
                {t("online.patientName")}
              </b>
              <span>{patient_name || t("common.na")}</span>
            </div>
            <div className={styles["patientInfo"]}>
              <b>
                {t("prescription.preview.date")}
              </b>
              <span>
                {prescription_date
                  ? tzDateNumeric(new Date(
                      prescription_date + "T00:00:00",
                    ))
                  : tzDateNumeric(new Date())}
              </span>
            </div>
          </div>

          <div className={styles["patientInfo"]}>
            <b>
              {t("prescription.preview.dateOfBirth")}
            </b>
            <span>{date_of_birth || t("common.na")}</span>
          </div>
          <div className={styles["patientInfo"]}>
            <b>{t("prescription.create.gender")}</b>
            {gender || t("common.na")}
          </div>
          {/* Guardian — shown only for child prescriptions. */}
          {guardian_name && (
            <div className={styles["patientInfo"]}>
              <b>
                {t("online.guardianName")}
              </b>
              <span>{guardian_name}</span>
            </div>
          )}
        </div>

        <hr />

        {prescriptionType === "medication" && (
          <>
            <div className={styles["diagnosisBar"]}>
              <h2>
                {t("prescription.preview.diagnosis")}
              </h2>
              <span>{medical_condition || t("common.na")}</span>
            </div>

            <hr />

            <div className={styles["medicationSec"]}>
              <h2>
                {t("prescription.create.tab.medication")}
              </h2>
              <div className={styles["medTableWrapper"]}>
                <table>
                  <thead>
                    <tr>
                      <th>
                        {t("prescription.preview.column.medicine")}
                      </th>
                      <th>
                        {t("prescription.preview.column.category")}
                      </th>
                      <th>
                        {t("prescription.create.column.dosage")}
                      </th>
                      <th>
                        {t("prescription.create.column.frequency")}
                      </th>
                      <th>
                        {t("prescription.create.column.duration")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {medications.length > 0 ? (
                      medications.map((med, index) => {
                        // Format dosage: add "tablet" or "tablets"
                        let dosageText = med.dosage || "-";
                        if (med.dosage && med.dosage !== "-") {
                          const dosageStr = med.dosage.toString().toLowerCase();
                          const hasTabletWord = dosageStr.includes("tablet");
                          if (!hasTabletWord) {
                            const dosageValue = parseFloat(
                              med.dosage.toString().replace(/[^0-9.]/g, ""),
                            );
                            if (!isNaN(dosageValue)) {
                              dosageText =
                                dosageValue === 1
                                  ? t("prescription.preview.dosageTablet", {
                                      dosage: med.dosage,
                                    })
                                  : t("prescription.preview.dosageTablets", {
                                      dosage: med.dosage,
                                    });
                            }
                          }
                        }

                        // Format frequency: add "time" or "times"
                        let frequencyText = med.frequency || "-";
                        if (med.frequency && med.frequency !== "-") {
                          const frequencyStr = med.frequency
                            .toString()
                            .toLowerCase();
                          const hasTimeWord = frequencyStr.includes("time");
                          if (!hasTimeWord) {
                            const frequencyValue = parseFloat(
                              med.frequency.toString().replace(/[^0-9.]/g, ""),
                            );
                            if (!isNaN(frequencyValue)) {
                              frequencyText =
                                frequencyValue === 1
                                  ? t("prescription.preview.frequencyTime", {
                                      frequency: med.frequency,
                                    })
                                  : t("prescription.preview.frequencyTimes", {
                                      frequency: med.frequency,
                                    });
                            }
                          }
                        }

                        // Format duration: add "days"
                        let durationText = med.duration || "-";
                        if (med.duration && med.duration !== "-") {
                          const durationStr = med.duration
                            .toString()
                            .toLowerCase();
                          const hasDaysWord = durationStr.includes("day");
                          if (!hasDaysWord) {
                            durationText = t("prescription.preview.durationDays", {
                              duration: med.duration,
                            });
                          }
                        }

                        return (
                          <tr key={index}>
                            <td>{med.medication || "-"}</td>
                            <td>{med.category_name || "-"}</td>
                            <td>{dosageText}</td>
                            <td>{frequencyText}</td>
                            <td>{durationText}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>
                          {t("prescription.preview.noMedications")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <hr />

            {medications.map(
              (med, index) =>
                med.instruction && (
                  <div key={index} className={styles["instructionBlock"]}>
                    <h3>
                      {t("prescription.preview.instruction")}
                    </h3>
                    <p>{med.instruction}</p>
                    <hr />
                  </div>
                ),
            )}
          </>
        )}

        {prescriptionType === "lab" && (
          <>
            <div className={styles["diagnosisBar"]}>
              <h2>
                {t("prescription.preview.diagnosis")}
              </h2>
              <span>{medical_condition || t("common.na")}</span>
            </div>

            <hr />

            <div className={styles["labTestBlock"]}>
              <h2>
                {t("prescription.create.labTest")}
              </h2>
              {lab_tests.length > 0 ? (
                lab_tests.map((test, index) => (
                  <p key={index}>{test.test_name || t("common.na")}</p>
                ))
              ) : (
                <p>
                  {t("prescription.preview.noLabTests")}
                </p>
              )}
            </div>
            <hr />
            {lab_tests.map(
              (test, index) =>
                test.instruction && (
                  <div key={index} className={styles["instructionBlock"]}>
                    <h3>
                      {t("prescription.preview.instructionFor", {
                        name: test.test_name,
                      })}
                    </h3>
                    <p>{test.instruction}</p>
                    <hr />
                  </div>
                ),
            )}
          </>
        )}

        {prescriptionType === "imaging" && (
          <>
            <div className={styles["diagnosisBar"]}>
              <h2>
                {t("prescription.preview.diagnosis")}
              </h2>
              <span>{medical_condition || t("common.na")}</span>
            </div>

            <hr />

            <div className={styles["labTestBlock"]}>
              <h2>
                {t("prescription.preview.imagingTest")}
              </h2>
              {imaging_tests.length > 0 ? (
                imaging_tests.map((test, index) => (
                  <p key={index}>{test.test_name || t("common.na")}</p>
                ))
              ) : (
                <p>
                  {t("prescription.preview.noImagingTests")}
                </p>
              )}
            </div>

            <hr />

            {imaging_tests.map(
              (test, index) =>
                test.instruction && (
                  <div key={index} className={styles["instructionBlock"]}>
                    <h3>
                      {t("prescription.preview.instructionFor", {
                        name: test.test_name,
                      })}
                    </h3>
                    <p>{test.instruction}</p>
                    <hr />
                  </div>
                ),
            )}
          </>
        )}

        {prescriptionType === "physiotherapy" && (
          <>
            <div className={styles["diagnosisBar"]}>
              <h2>
                {t("prescription.preview.diagnosis")}
              </h2>
              <span>{medical_condition || t("common.na")}</span>
            </div>

            <hr />

            <div className={styles["labTestBlock"]}>
              <h2>
                {t("prescription.create.physiotherapyTest")}
              </h2>
              {physio_tests.length > 0 ? (
                physio_tests.map((test, index) => (
                  <p key={index}>{test.test_name || t("common.na")}</p>
                ))
              ) : (
                <p>
                  {t("prescription.preview.noPhysioTests")}
                </p>
              )}
            </div>

            <hr />

            {physio_tests.map(
              (test, index) =>
                test.instruction && (
                  <div key={index} className={styles["instructionBlock"]}>
                    <h3>
                      {t("prescription.preview.instructionFor", {
                        name: test.test_name,
                      })}
                    </h3>
                    <p>{test.instruction}</p>
                    <hr />
                  </div>
                ),
            )}
          </>
        )}

        {prescriptionType === "nurse" && (
          <>
            <div className={styles["diagnosisBar"]}>
              <h2>
                {t("prescription.preview.diagnosis")}
              </h2>
              <span>{medical_condition || t("common.na")}</span>
            </div>

            <hr />

            <div className={styles["labTestBlock"]}>
              <h2>
                {t("prescription.create.nurseService")}
              </h2>
              {nurse_tests.length > 0 ? (
                nurse_tests.map((test, index) => (
                  <p key={index}>{test.test_name || t("common.na")}</p>
                ))
              ) : (
                <p>
                  {t("prescription.preview.noNurseServices")}
                </p>
              )}
            </div>

            <hr />

            {nurse_tests.map(
              (test, index) =>
                test.instruction && (
                  <div key={index} className={styles["instructionBlock"]}>
                    <h3>
                      {t("prescription.preview.instructionFor", {
                        name: test.test_name,
                      })}
                    </h3>
                    <p>{test.instruction}</p>
                    <hr />
                  </div>
                ),
            )}
          </>
        )}

        <hr />

        {/* <div className={styles["drSignatureWrapper"]}>
          <img src={Signature} alt="" />
          <img src={Stamp} alt="" />
        </div> */}
        <div className={styles["drSignatureWrapper"]}>
          {(() => {
            const stampUrl = stamp_presigned_url || stamp;
            const signatureUrl = signature_presigned_url || signature;

            return (
              <>
                {stampUrl ? (
                  <img
                    key={stampUrl} // Force re-render if URL changes
                    src={stampUrl}
                    alt={t("prescription.create.stampAlt")}
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      console.error(
                        "Failed to load stamp image. URL:",
                        stampUrl,
                      );
                      console.error("Error details:", e);
                      // Fallback to placeholder
                      e.target.src = "/no_image.jpeg";
                      e.target.onerror = null; // Prevent infinite loop
                    }}
                    onLoad={() => {
                      console.log("Stamp image loaded successfully:", stampUrl);
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "inline-block",
                      width: "150px",
                      height: "150px",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: "12px",
                      textAlign: "center",
                      padding: "10px",
                    }}
                  >
                    {t("prescription.preview.noStamp")}
                  </div>
                )}
                {signatureUrl ? (
                  <img
                    key={signatureUrl} // Force re-render if URL changes
                    src={signatureUrl}
                    alt={t("prescription.create.signatureAlt")}
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      console.error(
                        "Failed to load signature image. URL:",
                        signatureUrl,
                      );
                      console.error("Error details:", e);
                      // Fallback to placeholder
                      e.target.src = "/no_image.jpeg";
                      e.target.onerror = null; // Prevent infinite loop
                    }}
                    onLoad={() => {
                      console.log(
                        "Signature image loaded successfully:",
                        signatureUrl,
                      );
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "inline-block",
                      width: "150px",
                      height: "150px",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: "12px",
                      textAlign: "center",
                      padding: "10px",
                    }}
                  >
                    {t("prescription.preview.noSignature")}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <div className={styles["fileCol"]}>
        <div className={styles["inputWrapper"]}>
          <label htmlFor="fileName">
            {t("prescription.preview.enterFileName")}
          </label>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className={styles["form-input"]}
          />
        </div>

        <button
          className={styles["btnGreen"]}
          onClick={onGenerateOnly ? handleGenerateOnly : handleSavePrescription}
          disabled={
            onGenerateOnly ? isGenerating : savePrescriptionState.loading
          }
        >
          {onGenerateOnly ? (
            isGenerating ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.8s linear infinite",
                  }}
                ></span>
                {t("prescription.preview.generating")}
              </span>
            ) : t("common.next")
          ) : savePrescriptionState.loading ? (
            t("common.saving")
          ) : t("common.next")}
        </button>
      </div>
    </div>
  );
}
